/// <amd-module name="Feed/Connect/Components/Common" />
import * as React from "react";

import { FormatComponent } from "VSS/Platform/Components/Format";
import { useComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";

import { Card } from "VSSUI/Card";
import { Image } from "VSSUI/Image";
import { Link } from "VSSUI/Link";
import { MessageBar } from "VSSUI/MessageBar";
import { css } from "VSSUI/Util";

import * as Res from "Feed/Connect/Resources";
import { IUxFeedView } from "Feed/Core/UxFeedView";

import { ITool } from "../Tool";
import { CopyableText, ICopyableTextProps } from "./CopyableText";

export const CopyableCode: React.SFC<ICopyableTextProps> = props => (
    <CopyableText
        {...props}
        className={css("monospaced-m", props.className)}
        buttonProps={{ tooltipProps: { text: Res.CopyToClipboard }, ariaLabel: props.ariaLabel }}
    />
);

export const FWLink: React.FunctionComponent<{ id: string; underline?: boolean }> = ({ id, underline, children }) => (
    <Link
        href={`https://go.microsoft.com/fwlink/?linkid=${id}`}
        target="_blank"
        rel="nofollow noreferrer noopener help"
        underline={underline}
    >
        {children}
    </Link>
);

interface IDetailsCardTitleProps {
    text: string;
    ariaLevel?: number;
}

export const DetailsCard: React.FunctionComponent<{ title: IDetailsCardTitleProps }> = ({ title, children }) => (
    <Card
        className="card"
        titleProps={{ text: title.text, className: "font-weight-semibold", ariaLevel: title.ariaLevel }}
    >
        <div className="flex-grow card-content">{children}</div>
    </Card>
);

export const PublishDetailsCard: React.FunctionComponent<{ feed: IUxFeedView }> = ({ feed, children }) => {
    const parts = feed.name.split("@");
    if (parts.length === 2) {
        return (
            <DetailsCard title={{ text: Res.PublishPackages, ariaLevel: 3 }}>
                <MessageBar className="messagebar">
                    <FormatComponent format={Res.PublishNotSupported}>
                        @{parts[1]}
                        {parts[0]}
                    </FormatComponent>
                </MessageBar>
            </DetailsCard>
        );
    } else {
        return <DetailsCard title={{ text: Res.PublishPackages, ariaLevel: 3 }}>{children}</DetailsCard>;
    }
};

export const ToolIcon: React.FC<{ tool: ITool; className?: string }> = ({ tool, className }) => {
    const context = useComponentContext();
    return (
        <Image
            {...tool.imageProps}
            src={getAssetLocation(context.pageContext, tool.imageProps.src)}
            className={css(tool.imageProps.className, className)}
            alt={tool.name}
        />
    );
};

/**
 * WidthBreakpoint renders child elements only if inner width is less than provided as widthLessThan prop
 */
export const WidthBreakpoint: React.FC<{ widthLessThan: number }> = ({ children, widthLessThan }) => {
    const [width, setWidth] = React.useState<number>(window.innerWidth);

    React.useEffect(() => {
        const onResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    });

    return <>{width < widthLessThan ? children : ""}</>;
};
